import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { en, type MessageKey } from './en'
import { vi } from './vi'

// ─────────────────────────────────────────────────────────────────────────────
// MULTI-LANGUAGE UI.
//
// EVERY user-visible string goes through here. Nothing in src/ is allowed to
// hardcode a sentence a visitor can read — the whole point is that adding a
// language stays a one-file job (drop a dictionary next to vi.ts, add it to
// LOCALES) instead of a hunt through components.
//
// The locale is remembered per browser in localStorage, and defaults to the
// visitor's own browser language on the first visit. It is NOT stored on the
// server: a language choice is a display preference, not account data, and
// keeping it local means it works before anyone signs in.
//
// `<html lang>` is kept in sync because screen readers pick pronunciation from
// it, and Google treats it as the page's declared language.
// ─────────────────────────────────────────────────────────────────────────────

/** Add a locale here and in the dictionary map below. Nothing else changes. */
export const LOCALES = {
  en: 'English',
  vi: 'Tiếng Việt',
} as const

export type Locale = keyof typeof LOCALES

const DICTIONARIES: Record<Locale, Record<MessageKey, string>> = { en, vi }

/** `html lang` / `og:locale` value for each locale. */
const HTML_LANG: Record<Locale, string> = { en: 'en', vi: 'vi' }

const STORAGE_KEY = 'upbo:locale'
const FALLBACK: Locale = 'en'

const isLocale = (value: unknown): value is Locale =>
  typeof value === 'string' && value in LOCALES

/**
 * Stored choice first, then the browser's own languages, then English.
 *
 * Wrapped in try/catch because localStorage throws outright in a browser set
 * to block site data — a language preference must never be the reason a page
 * fails to render.
 */
function detectLocale(): Locale {
  try {
    const saved = localStorage.getItem(STORAGE_KEY)
    if (isLocale(saved)) return saved
  } catch {
    /* private mode / storage blocked */
  }
  if (typeof navigator !== 'undefined') {
    for (const tag of navigator.languages ?? [navigator.language]) {
      const base = tag.toLowerCase().split('-')[0]
      if (isLocale(base)) return base
    }
  }
  return FALLBACK
}

export type Translate = (key: MessageKey, vars?: Record<string, string | number>) => string

interface I18nValue {
  locale: Locale
  setLocale: (locale: Locale) => void
  t: Translate
}

const I18nContext = createContext<I18nValue | null>(null)

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(detectLocale)

  useEffect(() => {
    document.documentElement.lang = HTML_LANG[locale]
  }, [locale])

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next)
    try {
      localStorage.setItem(STORAGE_KEY, next)
    } catch {
      /* the choice still applies to this tab */
    }
  }, [])

  const t = useCallback<Translate>(
    (key, vars) => {
      // Fall through to English rather than showing the raw key: a locale that
      // is one string behind should read imperfectly, not look broken.
      const template = DICTIONARIES[locale][key] ?? en[key] ?? key
      if (!vars) return template
      return template.replace(/\{(\w+)\}/g, (whole, name: string) =>
        name in vars ? String(vars[name]) : whole,
      )
    },
    [locale],
  )

  const value = useMemo<I18nValue>(() => ({ locale, setLocale, t }), [locale, setLocale, t])
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
}

export function useI18n(): I18nValue {
  const ctx = useContext(I18nContext)
  if (!ctx) throw new Error('useI18n must be used inside <I18nProvider> — see src/main.tsx')
  return ctx
}

/**
 * Turn a failed request into a sentence in the visitor's language.
 *
 * The server sends a stable `code` next to its own English message
 * (server/router.ts). A code we have a translation for wins; anything else
 * falls back to the server's message, so a new endpoint's error is readable
 * the moment it ships and only needs a dictionary entry to be localized.
 */
export function translateError(error: unknown, t: Translate, fallback: string): string {
  const code = (error as { code?: unknown } | null)?.code
  if (typeof code === 'string') {
    const key = `error.${code}` as MessageKey
    if (key in en) return t(key)
  }
  const message = (error as { message?: unknown } | null)?.message
  return typeof message === 'string' && message ? message : fallback
}

export type { MessageKey }
